import { useState, useEffect, useCallback } from "react";
import APIutil from "api/APIutils";
import * as url from "api/url"
import { userAccessPermission, hubServerErrors, formErrorMessages } from "./constants";
import { useSelector, useDispatch } from "react-redux";
import { getAllResources } from 'action/cultureAction';
import {sendSingleAmplitudeEvent} from "amplitude/amplitude";
import { useDateTimeByUserFormat } from "utils";

export const useFetchHubParamsData = (history) => {
    const { getMultipleErrorString } = useGetLocalizationLabel();
    const [state, setState] = useState({
        data: {},
        loading: false,
        error: false,
        errorMessage: "",
        certificateDetails: {
            rootCertDetails: null,
            issuerCertDetails: null,
            operationCertDetails: null
        },
        applicationAccess: null,
        fileUploadError: "",
        fileUploading: false,
        hubRootCertName: "",
        hubOperationCertName: "",
        hubServerStatus: {}
    });
    const { getDateTimeValue } = useDateTimeByUserFormat();
    
    useEffect(() => {
        getApplicationAccess();
        sendSingleAmplitudeEvent("ViewHubParameters"); 
    }, []);

    useEffect(() => {
        if (state.applicationAccess !== null && typeof state.applicationAccess === "string" && !state.applicationAccess.includes(`${userAccessPermission.None}`)) {
            fetchData();
            getCertificateDetails();
        }
    }, [state.applicationAccess]);

    const getApplicationAccess = useCallback(() => {
        setState(prevState => ({ ...prevState, loading: true, error: false, errorMessage: "" }));
        APIutil.getApplicationPermission(20).then(response => {
            if (response && response.data) {
                setState(prevState => ({ 
                    ...prevState,
                    loading: false,
                    applicationAccess: response.data.actionNumbers !== "" ? response.data.actionNumbers : "0"
                }));
            } else if (response && response.status === 401) {
                setState(prevState => ({ ...prevState, loading: false }));
                history.push("/UserNotAuthorized");
            } else {
                const message = response && response.statusText ? response.statusText : "Something went wrong. Please try refreshing the page."
                setState(prevState => ({ ...prevState, loading: false, error:true, errorMessage: message }));
            }
        }).catch(error => {
            setState(prevState => ({ ...prevState, loading: false, error: true, errorMessage: "Something went wrong. Please try refreshing the page." }));
            throw error;
        })
    }, [setState]);

    const fetchData = useCallback((payload, suppressError) => {
        if (payload && (payload.editMode)) {
            return null;
        } else {
            setState(prevState => ({ ...prevState, loading: true }));
            APIutil.getDataAPI(url.get_hub_parameter_details).then(response => {
                if (response && !response.statusText) {
                    if (response.hubMode === "1") {
                        setState(prevState => ({ ...prevState,
                            loading: false,
                            data: response,
                            hubRootCertName: response.rootCertificate ? `${response.rootCertificate} expires on ${response.rootCertificateValidity}` : "",
                            hubOperationCertName: response.operationalCertificate ? `${response.operationalCertificate} expires on ${response.operationalCertificateValidity}` : "",
                            error: false,
                            errorMessage: ""
                        }));
                    } else {
                        setState(prevState => ({ ...prevState, loading: false, data: response, hubRootCertName: "", hubOperationCertName: "" }));
                    }
                } else {
                    const message = response && response.statusText ? response.statusText : "Something went wrong. Please try refreshing the page."
                    if (suppressError) {
                        setState(prevState => ({ ...prevState, loading: false }));
                    } else {
                        setState(prevState => ({ ...prevState, loading: false, error:true, errorMessage: message }));
                    }
                }
            }).catch(error => {
                if (suppressError) {
                    setState(prevState => ({ ...prevState, loading: false }));
                } else {
                    setState(prevState => ({ ...prevState, loading: false, error: true, errorMessage: "Something went wrong. Please try refreshing the page." }));
                }
                throw error;
            });
        }
    }, [setState]);

    const submitForm = useCallback((payload, callbackFunction, fileUploadKey, mode) => {
        let urlValue = url.update_hub_parameter_details;
        if (fileUploadKey) {
            urlValue = fileUploadKey === "rootCertificateRequest" ? `${url.upload_root_certificate}/${Number(mode)}` : `${url.upload_operational_certificate}/${Number(mode)}`;
            setState(prevState => ({ ...prevState, fileUploadError: "", fileUploading: true }));
        } else {
            setState(prevState => ({ ...prevState, loading: true, error: false, errorMessage: "" }));
        }
        APIutil.postImage(urlValue, payload).then(response => {
            if (response && response.data && response.data.errorCode === "ERRCE0000" && fileUploadKey) {
                callbackFunction && callbackFunction("success");
                const certificateName = `${response.data.certSubject} expires on ${getDateTimeValue(response.data.validTo)}`;
                if (fileUploadKey === "rootCertificateRequest") {
                    setState(prevState => ({ ...prevState, fileUploading: false, hubRootCertName: certificateName }));
                } else {
                    setState(prevState => ({ ...prevState, fileUploading: false, hubOperationCertName: certificateName }));
                }
            } else if(!fileUploadKey && response && response.status === 200){
                callbackFunction && callbackFunction("success");
                setState(prevState => ({ ...prevState, loading: false }));
            } else {
                const errorValue = response.statusText ? response.statusText : "Something went wrong please try again.";
                if (fileUploadKey) {
                    setState(prevState => ({ ...prevState,
                        fileUploadError: response.data.errorCode ? getMultipleErrorString(response.data.errorCode, ",") :  errorValue,
                        fileUploading: false }));
                } else {
                    setState(prevState => ({ ...prevState, loading: false, errorMessage: errorValue, error: true }));
                }
                callbackFunction && callbackFunction("error", { message: errorValue });
            }
        }).catch(error => {
            if (fileUploadKey) {
                setState(prevState => ({ ...prevState, fileUploadError: "Something went wrong please try again.", fileUploading: false }));
            } else {
                setState(prevState => ({ ...prevState, loading: false, errorMessage: "Something went wrong please try again.", error: true }));
            }
            callbackFunction && callbackFunction("error", error);
            throw error;
        });
    }, [setState]);

    const getCertificateDetails = useCallback((payload, suppressError) => {
        if (payload && (payload.editMode)) {
            return null;
        } else {
            setState(prevState => ({ ...prevState, loading: true }));
            Promise.all([
                APIutil.getDataAPI(url.get_root_cert_details), 
                APIutil.getDataAPI(url.get_issuer_cert_details),
                APIutil.getDataAPI(url.get_operational_cert_details),
                APIutil.getDataAPI(url.get_hub_server_status)
                ]).then(([response1, response2, response3, response4]) => {
                    setState(prevState => ({ ...prevState, loading: false, certificateDetails: {
                        rootCertDetails: (response1 && !response1.statusText) ? response1 : null,
                        issuerCertDetails: (response2 && !response2.statusText) ? response2 : null,
                        operationCertDetails: (response3 && !response3.statusText) ? response3 : null
                    },
                    hubServerStatus: (response4 && !response4.statusText) ? response4 : {}
                }));
            }).catch(error => {
                if (suppressError) {
                    setState(prevState => ({ ...prevState, loading: false }));
                } else {
                    setState(prevState => ({ ...prevState, loading: false, error: true, errorMessage: "Unable to get certificate details. Please try refreshing the page." }));
                }
                throw error;
            });
        }
    }, [setState]);

    const downloadRootCertificate = useCallback((fileName, isCSRCertificate) => {
        setState(prevState => ({ ...prevState, error: false, errorMessage: "" }));
        const urlLink = isCSRCertificate ? url.generate_new_csr_certificate : url.download_root_certificate;
        return APIutil.getPDFDataAPI(urlLink)
            .then(response => {
                if (response && !response.statusText) {
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(new Blob([response]), fileName);
                    } else {
                        const url = window.URL.createObjectURL(new Blob([response]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', fileName);
                        document.body.appendChild(link);
                        link.click();
                    }
                } else {
                    const messageContent = isCSRCertificate ? "Unable to generate new CSR certificate. Please try again." : "Unable to download root certificate details. Please try again."
                    setState(prevState => ({ ...prevState, error: true, errorMessage: messageContent }));
                }
            })
            .catch(error => {
                const messageContent = isCSRCertificate ? "Unable to generate new CSR certificate. Please try again." : "Unable to download root certificate details. Please try again."
                setState(prevState => ({ ...prevState, error: true, errorMessage: messageContent }));
                throw error;
            })
    }, [setState]);
    
    return {
        data: state.data,
        loading: state.loading,
        error: state.error,
        errorMessage: state.errorMessage,
        refetch: fetchData,
        submitForm,
        setAPIHookData: setState,
        certificateDetails: state.certificateDetails,
        getCertificateDetails,
        downloadRootCertificate,
        getApplicationAccess,
        applicationAccess: state.applicationAccess,
        fileUploadError: state.fileUploadError,
        fileUploading: state.fileUploading,
        hubRootCertName: state.hubRootCertName,
        hubOperationCertName: state.hubOperationCertName,
        hubServerStatus: state.hubServerStatus
    };
}

export const useGetLocalizationLabel = () => {
    const systemAdminLabels = useSelector(state => state.CulturalLabelsRootReducer.SystemAdmin_LabelsBasedOnPreference);
    const dispatch = useDispatch();
    useEffect(() => {
        if (systemAdminLabels["ViewHubParameters.ViewHeaderTitle.Text"] === undefined) {
            dispatch(getAllResources());
        }
    }, []);

    const getLabel = (labelKey) => {
        if (systemAdminLabels && systemAdminLabels[`ViewHubParameters.${labelKey}.Text`]) {
            return systemAdminLabels[`ViewHubParameters.${labelKey}.Text`];
        } else {
            return "";
        }
    }

    const getAPIErrorLabel = (errorKey) => {
        if (systemAdminLabels && systemAdminLabels[`ViewHubParameters.Error.${errorKey}.Text`]) {
            return systemAdminLabels[`ViewHubParameters.Error.${errorKey}.Text`];
        } else {
            return hubServerErrors[errorKey] ? hubServerErrors[errorKey] : "Undefined error";
        }
    };

    const getFormErrorLabel = (errorKey) => {
        if (systemAdminLabels && systemAdminLabels[`ViewHubParameters.Error.${errorKey}.Text`]) {
            return systemAdminLabels[`ViewHubParameters.Error.${errorKey}.Text`];
        } else {
            return formErrorMessages[errorKey] ? formErrorMessages[errorKey] : "Invalid Input";
        }
    };

    const getMultipleErrorString = (stringData, splitBy=",", replaceBy=" ") => {
        const searchRegExp = / /gi;
        if (stringData && stringData.length && splitBy) {
            let newErrorString = "";
            const updatedString = stringData.replace(searchRegExp, "");
            updatedString.split(splitBy).map(errorCode => {
                if (newErrorString === "") {
                    newErrorString = `${getAPIErrorLabel(errorCode)}`;
                } else {
                    newErrorString = `${newErrorString}, ${getAPIErrorLabel(errorCode)}`;
                }
                return errorCode;
            });
            return newErrorString;
        } else {
            return getAPIErrorLabel(stringData.replace(searchRegExp, ""));
        }
    }

    return {
        hubServerErrors,
        formErrorMessages,
        systemAdminLabels,
        getLabel,
        getFormErrorLabel,
        getAPIErrorLabel,
        getMultipleErrorString
    }
}
